import {SetSlotAvailabilityOptions} from '../context/SlotManagementContext'
import {SlotValidationResponse} from '../requests/types'

/**
 * Base query/mutation options with callbacks
 */
export interface BaseSlotValidationOptions {
  onSuccess?: (data: SlotValidationResponse) => void
  onError?: (error: unknown) => void
  [key: string]: unknown
}

/**
 * Composes consumer callbacks with internal state update side effects.
 *
 * Pattern:
 * 1. Call consumer's callback (if provided)
 * 2. Update slot availability state
 *
 * This ensures both query and mutation variants have identical side effects.
 *
 * @param baseOptions - Original query/mutation options from consumer
 * @param dateTimeIndex - Index of the dateTime being validated
 * @param setSlotAvailability - State updater from context
 * @returns Options with composed callbacks
 */
export const composeSlotValidationCallbacks = <T extends BaseSlotValidationOptions>(
  baseOptions: T,
  dateTimeIndex: number,
  setSlotAvailability: (options: SetSlotAvailabilityOptions) => void
): T => {
  return {
    ...baseOptions,
    // Compose consumer's onSuccess with internal state update
    onSuccess: (data: SlotValidationResponse) => {
      // First call consumer's callback if provided
      baseOptions.onSuccess?.(data)

      // Then update internal state
      setSlotAvailability({
        dateTimeIndex,
        isAvailable: data.valid
      })
    },
    // Compose consumer's onError with internal state update
    onError: (error: unknown) => {
      // First call consumer's callback if provided
      baseOptions.onError?.(error)

      // Then mark slot as unavailable
      setSlotAvailability({
        dateTimeIndex,
        isAvailable: false
      })
    }
  }
}
